import React, { Suspense } from 'react'
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import DashboardLayout from './components/layout/DashboardLayout'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { CustomerScopeProvider } from './contexts/CustomerScopeContext'
import { ThemeProvider } from './contexts/ThemeContext'
import Login from './pages/Login'
import ResetPassword from './pages/ResetPassword'
import { Lasteikon, Lasteside } from './components/Lasteikon'
import { useStiEtterInnlogging } from './hooks/useStiEtterInnlogging'
import ErrorBoundary from './components/ErrorBoundary'
import Forside, { MarkerLandet } from './pages/Forside'
import { isPublicCustomerPath } from './lib/publicRoutes'
import { lastSide } from './lib/lastSide'

// Shared React Query client.
// - staleTime 60s: navigating back to a page within 60s returns cached data
//   without hitting the backend
// - gcTime 5min: keep cached data for 5 minutes after last component unmounts
// - refetchOnWindowFocus false: avoid bursts of refetches when user tab-switches
// - retry 1: one retry on transient network errors
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})

// Sidene lastes når de åpnes (src/lib/lastSide.js). Login, ResetPassword og
// Forside lastes med en gang: de vises før eller rundt alt annet.
const DashboardSide = lastSide(() => import('./pages/dashboard/DashboardSide'))
const Inventory = lastSide(() => import('./pages/Inventory'))
const Orders = lastSide(() => import('./pages/Orders'))
const Analytics = lastSide(() => import('./pages/Analytics'))
const DeliveryMapPage = lastSide(() => import('./pages/DeliveryMapPage'))
const MinSide = lastSide(() => import('./pages/MinSide'))
const Finance = lastSide(() => import('./pages/Finance'))
const ForkliftInspection = lastSide(() => import('./pages/ForkliftInspection'))
const Settings = lastSide(() => import('./pages/Settings'))
const Varsler = lastSide(() => import('./pages/Varsler'))
const SafetyRound = lastSide(() => import('./pages/SafetyRound'))
const Receipts = lastSide(() => import('./pages/Receipts'))
const VaktplanSide = lastSide(() => import('./pages/vaktplan/Vaktplan'))
const VaktplanAdminSide = lastSide(() => import('./pages/vaktplan/VaktplanAdmin'))
const TimerSide = lastSide(() => import('./pages/vaktplan/Timer'))
const Articles = lastSide(() => import('./pages/Articles'))
const WmsOrders = lastSide(() => import('./pages/WmsOrders'))
const Henting = lastSide(() => import('./pages/Henting'))
const ProfitLoss = lastSide(() => import('./pages/ProfitLoss'))
const Balance = lastSide(() => import('./pages/Balance'))
const Billing = lastSide(() => import('./pages/Billing'))
const Locations = lastSide(() => import('./pages/Locations'))
const Scanner = lastSide(() => import('./pages/Scanner'))
const ScannerMove = lastSide(() => import('./pages/scanner/ScannerMove'))
const ScannerPick = lastSide(() => import('./pages/scanner/ScannerPick'))
const ScannerReceive = lastSide(() => import('./pages/scanner/ScannerReceive'))
const Incidents = lastSide(() => import('./pages/Incidents'))
const HmsOverview = lastSide(() => import('./pages/HmsOverview'))
const CreateOrder = lastSide(() => import('./pages/CreateOrder'))
const Users = lastSide(() => import('./pages/Users'))
const RolePermissions = lastSide(() => import('./pages/RolePermissions'))
const KundeDashboard = lastSide(() => import('./pages/kunde/KundeDashboard'))
const KundeOrdre = lastSide(() => import('./pages/kunde/KundeOrdre'))
const KundeAvtaler = lastSide(() => import('./pages/kunde/KundeAvtaler'))
const KundeAvtale = lastSide(() => import('./pages/kunde/KundeAvtale'))
const KundeFakturaer = lastSide(() => import('./pages/kunde/KundeFakturaer'))
const KundePrisoppslag = lastSide(() => import('./pages/kunde/KundePrisoppslag'))
const KundePriser = lastSide(() => import('./pages/kunde/KundePriser'))
const Customers = lastSide(() => import('./pages/Customers'))
const CustomerDetail = lastSide(() => import('./pages/CustomerDetail'))
const CollixlOrders = lastSide(() => import('./pages/CollixlOrders'))
const CollixlOrderDetail = lastSide(() => import('./pages/CollixlOrderDetail'))
const Mottak = lastSide(() => import('./pages/Mottak'))
const CrmOverview = lastSide(() => import('./pages/crm/CrmOverview'))
const CrmCompanies = lastSide(() => import('./pages/crm/CrmCompanies'))
const CrmCompanyDetail = lastSide(() => import('./pages/crm/CrmCompanyDetail'))
const CrmLeads = lastSide(() => import('./pages/crm/CrmLeads'))
const CrmQuotes = lastSide(() => import('./pages/crm/CrmQuotes'))
const CrmQuoteDetail = lastSide(() => import('./pages/crm/CrmQuoteDetail'))
const CrmQuoteSettings = lastSide(() => import('./pages/crm/CrmQuoteSettings'))
const CrmOfferRequests = lastSide(() => import('./pages/crm/CrmOfferRequests'))
const Innboks = lastSide(() => import('./pages/Innboks'))
const InnboksRegler = lastSide(() => import('./pages/InnboksRegler'))
const CustomerOffers = lastSide(() => import('./pages/CustomerOffers'))
const OfferPortalPage = lastSide(() => import('./pages/public/OfferPortalPage'))
const CrmPriceList = lastSide(() => import('./pages/crm/CrmPriceList'))
const Transportpriser = lastSide(() => import('./pages/crm/Transportpriser'))
const CrmProspectSearch = lastSide(() => import('./pages/crm/CrmProspectSearch'))
const DocumentParsers = lastSide(() => import('./pages/DocumentParsers'))
const Documents = lastSide(() => import('./pages/Documents'))
const Docs = lastSide(() => import('./pages/Docs'))
const TripletexImport = lastSide(() => import('./pages/TripletexImport'))
const Vehicles = lastSide(() => import('./pages/Vehicles'))
const Screens = lastSide(() => import('./pages/Screens'))
const Drift = lastSide(() => import('./pages/Drift'))
const SkjermKoble = lastSide(() => import('./pages/SkjermKoble'))
const Messages = lastSide(() => import('./pages/Messages'))
const OrderRules = lastSide(() => import('./pages/OrderRules'))
const ReturnOrderSync = lastSide(() => import('./pages/ReturnOrderSync'))
const LiabilityWaivers = lastSide(() => import('./pages/LiabilityWaivers'))
const TerminalScreen = lastSide(() => import('./pages/screens/TerminalScreen'))
const VaremottakScreen = lastSide(() => import('./pages/screens/VaremottakScreen'))
const KundeskjermScreen = lastSide(() => import('./pages/screens/KundeskjermScreen'))
const HenteskjermScreen = lastSide(() => import('./pages/screens/HenteskjermScreen'))
const LoungeScreen = lastSide(() => import('./pages/screens/LoungeScreen'))
const MoteromScreen = lastSide(() => import('./pages/screens/MoteromScreen'))
const QuoteAccept = lastSide(() => import('./pages/public/QuoteAccept'))
const Surveys = lastSide(() => import('./pages/Surveys'))
const Tracking = lastSide(() => import('./pages/Tracking'))
const Sms = lastSide(() => import('./pages/Sms'))
const TrackPage = lastSide(() => import('./pages/public/TrackPage'))
const OrderPortalPage = lastSide(() => import('./pages/public/OrderPortalPage'))
const SurveyPage = lastSide(() => import('./pages/public/SurveyPage'))
const LiabilityWaiverSign = lastSide(() => import('./pages/public/LiabilityWaiverSign'))

// RequirePermission guard'er en route basert på én permission-nøkkel
// (f.eks. 'orders.view'). Admin har alltid tilgang (håndtert inne i
// useAuth.hasPermission). Hvis brukeren mangler permission, sendes de
// tilbake til oversikten — bruker vi Navigate til "/" for kunder havner
// de i en evig loop, så vi sjekker rollen og sender dem til /kunde
// i stedet.
// Bevarer :id gjennom redirecten fra den gamle /avvik-stien.
function AvvikIdRedirect() {
  const { id } = useParams()
  return <Navigate to={`/mottak/${id}`} replace />
}

function RequirePermission({ permission, children }) {
  const { hasPermission, role } = useAuth()
  if (!hasPermission(permission)) {
    return <Navigate to={role === 'kunde' ? '/kunde/start' : '/'} replace />
  }
  return children
}

// Første side kunden har tilgang til. RequirePermission sender kunder hit, ikke
// til /kunde: uten kunde.dashboard ville /kunde ellers sendt kunden til seg selv
// i en evig løkke. «Be om tilbud» har ingen egen nøkkel og står alltid åpen.
function KundeStart() {
  const { hasPermission } = useAuth()
  if (hasPermission('kunde.dashboard')) return <Navigate to="/kunde" replace />
  if (hasPermission('kunde.orders')) return <Navigate to="/kunde/ordre" replace />
  if (hasPermission('kunde.contract')) return <Navigate to="/kunde/avtaler" replace />
  if (hasPermission('kunde.invoices')) return <Navigate to="/kunde/fakturaer" replace />
  if (hasPermission('kunde.prices')) return <Navigate to="/kunde/priser" replace />
  return <Navigate to="/kunde/tilbud" replace />
}

// Sidemenyen står mens en side lastes; bare innholdet viser lasteikonet.
function SideInnhold({ children }) {
  return <Suspense fallback={<Lasteikon storrelse="side" />}>{children}</Suspense>
}

function AppRoutes() {
  const { user, role, loading, passwordRecovery, unauthorized } = useAuth()
  const location = useLocation()
  // Tilbake til siden innloggingen startet på (QR-koden fra en skjerm).
  useStiEtterInnlogging(Boolean(user && role))

  // Offentlige skjerm-views (info-TVer på lageret) — ingen auth, ingen
  // DashboardLayout. Sjekkes før alt annet slik at en Samsung-TV som
  // laster /skjerm/<slug> aldri trigger Supabase-flyten eller redirects.
  // Auth-bypass gjøres på backend i server/app.js (allow-list for
  // /api/screens/*) — frontend trenger bare å vise viewet.
  if (location.pathname.startsWith('/skjerm/')) {
    return (
      <Routes>
        <Route path="/skjerm/terminal" element={<TerminalScreen />} />
        <Route path="/skjerm/varemottak" element={<VaremottakScreen />} />
        <Route path="/skjerm/kundeskjerm" element={<KundeskjermScreen />} />
        <Route path="/skjerm/henting" element={<HenteskjermScreen />} />
        <Route path="/skjerm/lounge" element={<LoungeScreen />} />
        <Route path="/skjerm/moterom" element={<MoteromScreen />} />
        <Route
          path="*"
          element={
            <div style={{ background: '#0a0a0a', color: '#f5f5f5', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui', fontSize: 28 }}>
              Skjerm finnes ikke
            </div>
          }
        />
      </Routes>
    )
  }

  // Offentlig tilbudsportal — ingen auth, ingen DashboardLayout. Token i URL
  // verifiseres backend-side mot crm_quotes.accept_token (rate-limited).
  if (location.pathname.startsWith('/q/')) {
    return (
      <Routes>
        <Route path="/q/:token" element={<QuoteAccept />} />
      </Routes>
    )
  }

  // Public-ruter (T&T + survey) — skal ALDRI gate'es av auth.
  // Vi sjekker pathname direkte slik at vi ikke trenger å vente på AuthProvider
  // sitt loading-state for ikke-innloggede besøk fra SMS-lenker.
  if (isPublicCustomerPath(location.pathname)) {
    return (
      <Routes>
        <Route path="/t/:token" element={<TrackPage />} />
        <Route path="/t/:token/survey" element={<SurveyPage legacy />} />
        <Route path="/o/:token" element={<OrderPortalPage />} />
        <Route path="/feedback/:token" element={<SurveyPage />} />
        <Route path="/tilbud" element={<OfferPortalPage />} />
      </Routes>
    )
  }

  if (location.pathname.startsWith('/w/')) {
    return (
      <Routes>
        <Route path="/w/:token" element={<LiabilityWaiverSign />} />
      </Routes>
    )
  }

  if (loading) return <Lasteside />

  // Bruker kom fra "glemt passord"-e-post — tving dem gjennom reset-skjemaet
  // før de får resten av appen. Supabase har allerede satt en recovery-
  // session, så user !== null her.
  if (passwordRecovery) {
    return (
      <Routes>
        <Route path="*" element={<ResetPassword />} />
      </Routes>
    )
  }

  // Not logged in. `unauthorized` settes når backend nettopp avviste en
  // SSO-pålogging og AuthContext logget brukeren ut igjen — vi viser
  // banneret så de skjønner hvorfor de er tilbake på login-siden.
  if (!user) {
    return (
      <Routes>
        {/* Åpne ruter tilgjengelige uten innlogging — per nå kun reset-password
            dersom brukeren navigerer dit manuelt etter at session er utløpt. */}
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="*" element={<Login unauthorized={unauthorized} />} />
      </Routes>
    )
  }

  // Logged in but no role = not in app_users (faller normalt ikke hit
  // lenger fordi AuthContext signOut-er ved 403, men beholdt som
  // safety net hvis fremtidig kode skipper auto-signout).
  if (!role) return <Login unauthorized />

  // Authenticated, full-page feedback preview. Kept outside DashboardLayout
  // so operators see the same mobile presentation as the SMS recipient.
  if (location.pathname.startsWith('/feedback-preview/')) {
    return (
      <Routes>
        <Route
          path="/feedback-preview/:orderId"
          element={(
            <RequirePermission permission="surveys.view">
              <RequirePermission permission="collixl_orders.view">
                <SurveyPage preview />
              </RequirePermission>
            </RequirePermission>
          )}
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    )
  }

  // Kunde role: redirect to customer portal
  if (role === 'kunde') {
    return (
      <DashboardLayout>
        <SideInnhold>
          <Routes>
            <Route path="/kunde" element={<RequirePermission permission="kunde.dashboard"><KundeDashboard /></RequirePermission>} />
            <Route path="/kunde/ordre" element={<RequirePermission permission="kunde.orders"><KundeOrdre /></RequirePermission>} />
            <Route path="/kunde/avtaler" element={<RequirePermission permission="kunde.contract"><KundeAvtaler /></RequirePermission>} />
            <Route path="/kunde/avtaler/:nummer" element={<RequirePermission permission="kunde.contract"><KundeAvtale /></RequirePermission>} />
            <Route path="/kunde/fakturaer" element={<RequirePermission permission="kunde.invoices"><KundeFakturaer /></RequirePermission>} />
            <Route path="/kunde/priser" element={<RequirePermission permission="kunde.prices"><KundePriser /></RequirePermission>} />
            <Route path="/kunde/prisoppslag" element={<RequirePermission permission="kunde.orders"><KundePrisoppslag /></RequirePermission>} />
            <Route path="/kunde/tilbud" element={<CustomerOffers />} />
            <Route path="/kunde/start" element={<KundeStart />} />
            <Route path="/skjermer/koble" element={<SkjermKoble />} />
            {/* «Bytt passord» i menyen går hit; uten ruten havnet kunden på Dashboard. */}
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="*" element={<Navigate to="/kunde/start" replace />} />
          </Routes>
        </SideInnhold>
      </DashboardLayout>
    )
  }

  // Medarbeider: minimal ansatt-selvbetjening — kun Min side.
  if (role === 'medarbeider') {
    return (
      <DashboardLayout>
        <SideInnhold>
          <Routes>
            <Route path="/min-side" element={<MinSide />} />
            <Route path="/skjermer/koble" element={<SkjermKoble />} />
            <Route path="*" element={<Navigate to="/min-side" replace />} />
          </Routes>
        </SideInnhold>
      </DashboardLayout>
    )
  }

  // Ansatte, ledere og administratorer lander på Min side første gang per
  // innlogging (pages/Forside.jsx).
  return (
    <DashboardLayout>
      <MarkerLandet />
      <SideInnhold>
        <Routes>
          <Route path="/" element={<Forside><DashboardSide /></Forside>} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {/* Delivery */}
          <Route path="/orders" element={<RequirePermission permission="orders.view"><Orders /></RequirePermission>} />
          <Route path="/analytics" element={<RequirePermission permission="analytics.view"><Analytics /></RequirePermission>} />
          <Route path="/leveringskart" element={<RequirePermission permission="analytics.view"><DeliveryMapPage /></RequirePermission>} />
          <Route path="/vehicles" element={<RequirePermission permission="vehicles.view"><Vehicles /></RequirePermission>} />
          {/* Operations */}
          <Route path="/inventory" element={<RequirePermission permission="inventory.view"><Inventory /></RequirePermission>} />
          <Route path="/wms-orders" element={<RequirePermission permission="wms_orders.view"><WmsOrders /></RequirePermission>} />
          <Route path="/henting" element={<RequirePermission permission="henting.view"><Henting /></RequirePermission>} />
          <Route path="/receipts" element={<RequirePermission permission="receipts.view"><Receipts /></RequirePermission>} />
          <Route path="/articles" element={<RequirePermission permission="articles.view"><Articles /></RequirePermission>} />
          <Route path="/locations" element={<RequirePermission permission="locations.view"><Locations /></RequirePermission>} />
          <Route path="/scanner" element={<RequirePermission permission="scanner.view"><Scanner /></RequirePermission>} />
          <Route path="/scanner/pick" element={<RequirePermission permission="scanner.pick"><ScannerPick /></RequirePermission>} />
          <Route path="/scanner/move" element={<RequirePermission permission="scanner.move"><ScannerMove /></RequirePermission>} />
          <Route path="/scanner/receive" element={<RequirePermission permission="scanner.receive"><ScannerReceive /></RequirePermission>} />
          {/* Personal */}
          {/* Vaktplanen (erstatter Planday). Krever vaktplan.write til de ansatte får
              «Min vakt»; da åpnes den for vaktplan.read. Den gamle vaktplanen på
              /scheduling er fjernet, og gamle lenker sendes hit. */}
          <Route path="/scheduling" element={<Navigate to="/vaktplan" replace />} />
          <Route path="/vaktplan" element={<RequirePermission permission="vaktplan.write"><VaktplanSide /></RequirePermission>} />
          <Route path="/vaktplan/admin" element={<RequirePermission permission="vaktplan.admin"><VaktplanAdminSide /></RequirePermission>} />
          <Route path="/vaktplan/timer" element={<RequirePermission permission="vaktplan.write"><TimerSide /></RequirePermission>} />
          <Route path="/min-side" element={<RequirePermission permission="vaktplan.read"><MinSide /></RequirePermission>} />
          {/* HMS */}
          <Route path="/hms-overview" element={<RequirePermission permission="hms.overview"><HmsOverview /></RequirePermission>} />
          <Route path="/hms" element={<RequirePermission permission="hms.incidents"><Incidents /></RequirePermission>} />
          <Route path="/forklift" element={<RequirePermission permission="hms.forklift"><ForkliftInspection /></RequirePermission>} />
          <Route path="/safety-round" element={<RequirePermission permission="hms.safety_round"><SafetyRound /></RequirePermission>} />
          {/* Økonomi */}
          <Route path="/finance" element={<RequirePermission permission="finance.view"><Finance /></RequirePermission>} />
          <Route path="/profit-loss" element={<RequirePermission permission="finance.profit_loss"><ProfitLoss /></RequirePermission>} />
          <Route path="/balance" element={<RequirePermission permission="finance.balance"><Balance /></RequirePermission>} />
          <Route path="/billing" element={<RequirePermission permission="billing.view"><Billing /></RequirePermission>} />
          {/* Salg & CRM */}
          <Route path="/crm" element={<RequirePermission permission="crm.view"><CrmOverview /></RequirePermission>} />
          <Route path="/customers" element={<RequirePermission permission="customers.view"><Customers /></RequirePermission>} />
          <Route path="/customers/:id" element={<RequirePermission permission="customers.view"><CustomerDetail /></RequirePermission>} />
          <Route path="/collixl-orders" element={<RequirePermission permission="collixl_orders.view"><CollixlOrders /></RequirePermission>} />
          <Route path="/collixl-orders/:id" element={<RequirePermission permission="collixl_orders.view"><CollixlOrderDetail /></RequirePermission>} />
          <Route path="/mottak" element={<RequirePermission permission="collixl_orders.view"><Mottak /></RequirePermission>} />
          <Route path="/mottak/:id" element={<RequirePermission permission="collixl_orders.view"><Mottak /></RequirePermission>} />
          {/* Siden het /avvik en halv dag. Redirect framfor å brekke lenker
              noen alt har delt internt — og id-en må følge med, ellers havner
              en delt lenke til ett avvik på lista i stedet. */}
          <Route path="/avvik" element={<Navigate to="/mottak" replace />} />
          <Route path="/avvik/:id" element={<AvvikIdRedirect />} />
          <Route path="/crm/companies" element={<RequirePermission permission="crm.companies"><CrmCompanies /></RequirePermission>} />
          <Route path="/crm/companies/:id" element={<RequirePermission permission="crm.companies"><CrmCompanyDetail /></RequirePermission>} />
          <Route path="/crm/leads" element={<RequirePermission permission="crm.leads"><CrmLeads /></RequirePermission>} />
          <Route path="/crm/quotes" element={<RequirePermission permission="crm.quotes"><CrmQuotes /></RequirePermission>} />
          <Route path="/crm/offer-requests" element={<RequirePermission permission="crm.quotes"><CrmOfferRequests /></RequirePermission>} />
          {/* Skjemaene fra collixl.no. /innboks/:id åpner én (varslene og e-posten peker dit). */}
          <Route path="/innboks/regler" element={<RequirePermission permission="innboks.view"><InnboksRegler /></RequirePermission>} />
          <Route path="/innboks/:id?" element={<RequirePermission permission="innboks.view"><Innboks /></RequirePermission>} />
          <Route path="/crm/quotes/innstillinger" element={<RequirePermission permission="crm.quotes"><CrmQuoteSettings /></RequirePermission>} />
          <Route path="/crm/quotes/:id" element={<RequirePermission permission="crm.quotes"><CrmQuoteDetail /></RequirePermission>} />
          <Route path="/crm/prospect-search" element={<RequirePermission permission="crm.prospect_search"><CrmProspectSearch /></RequirePermission>} />
          <Route path="/crm/price-list" element={<RequirePermission permission="crm.price_list"><CrmPriceList /></RequirePermission>} />
          <Route path="/crm/transportpriser" element={<RequirePermission permission="crm.transport_prices"><Transportpriser /></RequirePermission>} />
          {/* Admin */}
          <Route path="/settings" element={<RequirePermission permission="admin.settings"><Settings /></RequirePermission>} />
          {/* Ingen egen tilgang: serveren viser bare varslene brukerens tilganger dekker. */}
          <Route path="/varsler" element={<Varsler />} />
          <Route path="/settings/document-parsers" element={<RequirePermission permission="admin.settings"><DocumentParsers /></RequirePermission>} />
          <Route path="/settings/return-sync" element={<RequirePermission permission="admin.settings"><ReturnOrderSync /></RequirePermission>} />
          <Route path="/liability-waivers" element={<RequirePermission permission="admin.liability_waivers"><LiabilityWaivers /></RequirePermission>} />
          <Route path="/create-order" element={<RequirePermission permission="admin.create_order"><CreateOrder /></RequirePermission>} />
          <Route path="/users" element={<RequirePermission permission="admin.users"><Users /></RequirePermission>} />
          <Route path="/settings/roles" element={<RequirePermission permission="admin.users"><RolePermissions /></RequirePermission>} />
          <Route path="/documents" element={<RequirePermission permission="admin.documents"><Documents /></RequirePermission>} />
          <Route path="/docs" element={<RequirePermission permission="admin.docs"><Docs /></RequirePermission>} />
          <Route path="/tripletex-import" element={<RequirePermission permission="admin.tripletex_import"><TripletexImport /></RequirePermission>} />
          <Route path="/skjermer" element={<RequirePermission permission="admin.screens"><Screens /></RequirePermission>} />
          <Route path="/drift" element={<RequirePermission permission="admin.settings"><Drift /></RequirePermission>} />
          {/* Uten RequirePermission: en QR-kode skannet med feil bruker skal si
              hvem som er logget inn, ikke havne på forsiden. Siden sjekker
              admin.screens selv, og API-et krever den. */}
          <Route path="/skjermer/koble" element={<SkjermKoble />} />
          {/* Meldinger & Webhooks */}
          <Route path="/messages" element={<RequirePermission permission="messages.view"><Messages /></RequirePermission>} />
          <Route path="/order-rules" element={<RequirePermission permission="messages.view"><OrderRules /></RequirePermission>} />
          {/* Track & Trace + tilbakemeldinger */}
          <Route path="/tracking" element={<RequirePermission permission="tracking.admin"><Tracking /></RequirePermission>} />
          <Route path="/sms" element={<RequirePermission permission="tracking.admin"><Sms /></RequirePermission>} />
          <Route path="/surveys" element={<RequirePermission permission="surveys.view"><Surveys /></RequirePermission>} />
          {/* Kundesidene (/kunde) finnes bare i kundenes rutegruppe over: /api/kunde
              svarer bare rollen kunde. Administratorer ser dem med «Se som». */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </SideInnhold>
    </DashboardLayout>
  )
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <CustomerScopeProvider>
            <ErrorBoundary>
              <Suspense fallback={<Lasteside />}>
                <AppRoutes />
              </Suspense>
            </ErrorBoundary>
          </CustomerScopeProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  )
}

export default App
